import { useRef, type KeyboardEventHandler } from "react";

import { IconButton } from "@/components/ui/icon-button";
import { MuiSearchIcon } from "@/components/ui/mui-field-icons";
import { MUI_FIELD_BOX_MEDIUM, MUI_FIELD_PLACEHOLDER } from "@/components/ui/mui-field-shape";
import { MuiClearIcon } from "@/components/ui/mui-toolbar-icons";
import { cn } from "@/lib/utils";

// A search box, drawn the way the ported screens' search boxes are drawn.
//
// **Shared rather than page-local** (`docs/agents/migration-guidelines.md`,
// "Shared donor components port as shared Atlas components"). The donor draws
// this exact field twice from two files — the items list page's
// `ItemsSearchbox` and the search island's `SearchInput` — and both are MUI's
// default `OutlinedInput` with the theme adding no `MuiOutlinedInput` override,
// so MUI's defaults are the source spec:
//
//   - a **56px** control (MUI's default size, not the `small` 40px the selects
//     on the same screens use), 4px corners, white, 16px text, 14px of side
//     padding — `MUI_FIELD_BOX_MEDIUM`,
//   - a 24px magnifier in a start adornment, 8px clear of the text, in the
//     `action.active` ink MUI's `InputAdornment` uses (`InputAdornment.js:55`),
//   - a 40px round icon button at the end holding a 24px cross, drawn **only**
//     while the box holds something (`endAdornment: searchText !== '' && …`),
//     labelled `clear search` — the donor's own `aria-label`, verbatim.
//
// **Two sizes, because the donor draws two.** `size="small"` is the Spec list
// column filter's box (`ColumnFilterPopover.tsx`): `TextField size='small'` —
// 40px, the input's block padding 8.5px — with both glyphs at `fontSize='small'`
// (20px), and its clear button an `IconButton size='small'` (5px around the
// cross, 30px) that sits **inside** an end `InputAdornment`, so 8px clear of
// the text where the default box's is flush. Measured on the running donor.
//
// **It holds nothing.** The value comes down and the events go up. Typing and
// clearing go up *separately* — `onClear` is its own prop rather than an
// `onChange("")` — because the two are not the same event on every screen that
// uses this: one may debounce its typing and still apply a clear at once. Which
// of the two a screen wants is the screen's call, not this field's.

interface SearchFieldProps {
  value: string;
  onChange: (next: string) => void;
  /** A press of the clear button. Never reported as typing — see above. */
  onClear: () => void;
  placeholder: string;
  /**
   * The control's accessible name. Required, because the donor's box has only
   * a placeholder and a placeholder is not a name a screen reader keeps once
   * the user starts typing.
   */
  "aria-label": string;
  /** MUI's `size` — `"medium"` (56px) by default, `"small"` (40px). */
  size?: "medium" | "small";
  /** Focus the input as the field mounts (`TextField autoFocus`). */
  autoFocus?: boolean;
  /**
   * Whether a press on the clear button leaves focus in the box — the donor's
   * `onMouseDown={(event) => event.preventDefault()} // prevent onBlur`
   * (`SpecsListTableIsland.tsx:543`, `ProductFilters.tsx:112`,
   * `SpecSheetLineFilters.tsx:36`), so the next keystroke lands in the box
   * (#554 D2). **On by default**, as three of the four donor boxes have it.
   * The Spec list column filter's value search is the one without
   * (`ColumnFilterPopover.tsx:545-555`): its press takes focus, the button
   * unmounts with it, and the paper's trap takes it back.
   */
  keepsFocusOnClear?: boolean;
  /**
   * The clear button's name — the donor's `clear search` by default; the map's
   * search pill names it `Clear search` (`MapSearch.tsx:60`).
   */
  clearLabel?: string;
  /**
   * Whether the clear button sits in an end `InputAdornment`, 8px off the text
   * (`InputAdornment.js:69-71`) — always at `size="small"`, and at the default
   * size where the donor wraps its own button in one (Core Spec Links,
   * `CoreSpecLinks.tsx:329-338`) rather than passing the bare button.
   */
  clearAdornment?: boolean;
  /**
   * `"pill"` is the polygon map's search box (`MapSearch.tsx:35-67`): a small
   * field whose root and notch are rounded to 40px, MUI's default 24px
   * magnifier rather than the small field's 20px, and its clear button's `sx`
   * padding of 2px round the small cross. `"box"`, MUI's own corners, by default.
   */
  shape?: "box" | "pill";
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  className?: string;
  "data-testid"?: string;
}

export function SearchField({
  value,
  onChange,
  onClear,
  placeholder,
  "aria-label": ariaLabel,
  size = "medium",
  autoFocus,
  keepsFocusOnClear = true,
  clearLabel = "clear search",
  clearAdornment,
  shape = "box",
  onKeyDown,
  className,
  "data-testid": testId,
}: SearchFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const empty = value === "";
  const small = size === "small";
  const pill = shape === "pill";

  return (
    // **The whole field is the text box to the pointer**, as MUI's `InputBase`
    // root is: `cursor: text` (`InputBase.js:87`), and a click that lands on
    // the root itself focuses the input (`:437-440`, `event.currentTarget ===
    // event.target`). The bands above and below the text are the input's own
    // padding, as MUI's are, so what this reaches is the side padding and the
    // gap after the magnifier — dead under an arrow before (#576's parity
    // review). A click on the magnifier or the clear button is not on the
    // root, and does nothing here, as in MUI. No keyboard twin is needed: the
    // input is the tab stop.
    <div
      className={cn(
        MUI_FIELD_BOX_MEDIUM,
        small && "h-10",
        pill && "rounded-[40px]",
        "cursor-text",
        // **No end adornment, no root padding on the right.** The donor passes
        // `endAdornment: searchText !== '' && …`, so an empty field has none,
        // `adornedEnd`'s 14px never lands on the root (`OutlinedInput.js:94-98`)
        // and the input keeps its own right padding (`:135`) — the band is the
        // input's, so a press there does not blur a focused field. The input
        // takes the 14px below.
        empty && "pr-0",
        className,
      )}
      data-testid={testId}
      onClick={(event) => {
        if (event.currentTarget === event.target) inputRef.current?.focus();
      }}
    >
      {/* MUI's `InputAdornment position='start'`: `action.active`, 8px clear
          of the text. `aria-hidden` because the field is already named. */}
      <MuiSearchIcon className={cn("text-lennar-action-active mr-2", small && !pill && "size-5")} />

      {/* `type='text'`, not `search`: WebKit draws its own clear widget inside a
          search input, which would sit beside the donor's and clear the box
          without telling this component. */}
      <input
        ref={inputRef}
        type="text"
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        // The donor's `InputField` passes `autoComplete='off'` on every field
        // it draws.
        autoComplete="off"
        autoFocus={autoFocus}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
        // **MUI's input box, exactly.** `box-sizing: content-box; height:
        // 1.4375em` on `line-height: 1.4375em` (`InputBase.js:82,150-153`),
        // inside `padding: 16.5px` top and bottom (`OutlinedInput.js:135`) —
        // the text runs 16.5 to 39.5 in the 56px field and the input fills
        // the field's height, so a press anywhere in it places the caret. The
        // padding is written less the frame's border for the reason the side
        // padding is (`MUI_FIELD_FRAME_BORDER_PUBLISHED`), which also keeps the
        // text still when the focused 2px arrives.
        //
        // **`box-content!`, and the `!` is load-bearing.** The unlayered
        // `* { box-sizing: border-box }` in `index.css` outranks every plain
        // utility, and the border-box spelling of the same geometry — a
        // full-height input whose padding leaves 23px — is *not* the same to
        // Chrome: measured at 1440x900, it paints the text a device row lower
        // than the donor's content-box input does (ink rows 199-213 against
        // 198-212), with every computed box identical. Two wrong shapes came before it (#576's
        // parity review): a full-height input with no padding, where Chrome
        // centred the line itself and the ink landed a row low; and a bare
        // 23px input, which left the bands above and below the text outside
        // the input, where a press could neither place the caret nor start a
        // selection. `leading-*` follows `text-base` because tailwind-merge
        // drops one that precedes a conflicting `text-*`.
        // The placeholder is MUI's `InputBase` default — the shared field shape's,
        // which the box around this input cannot hand down (#535).
        // The small box's padding is `8.5px` (`OutlinedInput.js:144-148`), less
        // the same frame border.
        className={cn(
          "text-lennar-ink box-content! h-[1.4375em] min-w-0 flex-1 bg-transparent px-0 py-[calc(16.5px-var(--mui-field-frame-border))] text-base leading-[1.4375] outline-none",
          small && "py-[calc(8.5px-var(--mui-field-frame-border))]",
          MUI_FIELD_PLACEHOLDER,
          empty && "pr-[calc(14px-var(--mui-field-frame-border))]",
        )}
      />

      {empty ? null : (
        <IconButton
          aria-label={clearLabel}
          onClick={onClear}
          // A mouse press only, as the donor's: a keyboard press leaves focus on
          // the button, which then unmounts, in the donor too.
          onMouseDown={keepsFocusOnClear ? (event) => event.preventDefault() : undefined}
          // MUI's `IconButton` — its centred touch and focus ripple, round,
          // `action.active` ink and the standard hover tint: 8px of padding
          // around a 24px icon, or at `size='small'` 5px around a 20px one
          // (`IconButton.js:126-131`). No margin at the default size: the
          // donor's end adornment is the button itself, with the input's right
          // padding zeroed (`OutlinedInput.js:175`), so it sits flush against
          // the text. Small, it sits in an end `InputAdornment`, whose
          // `margin-left: 8px` (`InputAdornment.js:69-71`) is the gap, as it is
          // for a default-size donor box that wraps its button in one.
          size={small ? "small" : "medium"}
          className={cn((clearAdornment ?? small) && "ml-2", pill && "p-[2px]")}
        >
          <MuiClearIcon className={small ? "size-5" : undefined} />
        </IconButton>
      )}
    </div>
  );
}
